/**
 * A context-based hook that allows components to determine the view of the
 * "Incoming shipments" page. The API is the same as useContext.
 */

import React, { useState, useCallback, useContext, createContext } from "react";
import PropTypes from "prop-types";
import useLocalStorage from "./use-local-storage";
import { DefaultIncomingShipmentsUnscheduledTableColumns } from "../data/incoming-shipments-unscheduled-table-columns";
import incomingShipmentsTableColumns from "../data/incoming-shipments-table-columns";

const IncomingShipmentsViewPreferencesContext = createContext();

export const IncomingShipmentsViewPreferencesProvider = (props) => {
  const defaultSavedPreferencesConfig = new Map([
    ["stripes", true],
    [
      "columns",
      DefaultIncomingShipmentsUnscheduledTableColumns,
      incomingShipmentsTableColumns,
    ],
    ["new-view", true],
    ["expandAll", false],
    ["expandAllUnscheduled", false],
  ]);
  // Get shipment view preferences out of local storage
  const [savedPreferences, persistPreferences] = useLocalStorage(
    "incoming-shipments-view-preferences"
  );

  // Initialize state value falling back to the default values defined above
  const [preferences, setPreferences] = useState(
    // Revive preference map from local storage if it exists
    savedPreferences
      ? // add new columns to local storage if the user has outdated column structure
        new Map([
          ["stripes", new Map(savedPreferences).get("stripes")],
          [
            "columns",
            {
              ...defaultSavedPreferencesConfig.get("columns"),
              ...new Map(savedPreferences).get("columns"),
            },
          ],
          ["new-view", new Map(savedPreferences).get("new-view")],
          ["expandAll", new Map(savedPreferences).get("expandAll") || false],
          [
            "expandAllUnscheduled",
            new Map(savedPreferences).get("expandAllUnscheduled") || false,
          ],
        ])
      : defaultSavedPreferencesConfig
  );

  // When the user changes the preferences, persist the value to local storage
  // and update the state value
  const changePreferences = useCallback(
    (values) => {
      setPreferences(values);
      // Convert the values Map to an array when putting into local storage
      // so it can be serialized with JSON.stringify
      persistPreferences(Array.from(values.entries()));
    },
    [persistPreferences]
  );

  return (
    <IncomingShipmentsViewPreferencesContext.Provider
      value={[preferences, changePreferences]}
    >
      {props.children}
    </IncomingShipmentsViewPreferencesContext.Provider>
  );
};

IncomingShipmentsViewPreferencesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useIncomingShipmentsViewPreferences = () =>
  useContext(IncomingShipmentsViewPreferencesContext);

export default useIncomingShipmentsViewPreferences;